// ============================================================================
// RAID ASSIGN INLINE
//
// Mirrors RaidResolveInline: a card-level "Unowned"/owner label expands into a
// two-field form (name + org) rather than forcing the full edit modal open
// just to put a name against an item.
// ============================================================================

import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import { RaidOwnerOrg } from '../../../types/raid';
import { OWNER_ORGS } from './raidConfig';

export interface RaidAssignInlineProps {
  currentOwner?: string;
  currentOwnerOrg?: RaidOwnerOrg | '';
  saving?: boolean;
  onAssign: (owner: string, ownerOrg: RaidOwnerOrg | '') => void;
  onCancel: () => void;
}

export function RaidAssignInline({
  currentOwner, currentOwnerOrg, saving = false, onAssign, onCancel,
}: Readonly<RaidAssignInlineProps>) {
  const [owner, setOwner] = useState(currentOwner ?? '');
  const [ownerOrg, setOwnerOrg] = useState<RaidOwnerOrg | ''>(currentOwnerOrg ?? '');

  const inputClasses =
    'flex-1 min-w-[140px] border border-[#e2e8f0] rounded-xl px-3 py-1.5 text-xs text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
  const selectClasses =
    'border border-[#e2e8f0] rounded-xl px-2.5 py-1.5 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={owner}
        onChange={e => setOwner(e.target.value)}
        placeholder="Owner name"
        aria-label="Owner name"
        autoFocus
        className={inputClasses}
      />
      <select
        value={ownerOrg}
        onChange={e => setOwnerOrg(e.target.value as RaidOwnerOrg | '')}
        aria-label="Owner organisation"
        className={selectClasses}
      >
        <option value="">Unassigned org</option>
        {OWNER_ORGS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onAssign(owner.trim(), ownerOrg)}
          disabled={saving || !owner.trim()}
          className="flex items-center gap-1 rounded-xl px-3 py-1.5 bg-primary hover:bg-primary-dark text-white text-xs font-bold transition-colors disabled:opacity-60"
        >
          <Check size={12} /> Assign
        </button>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
